import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { HiltiMaterial, HiltiMaterialDisposal, HiltiProduct } from '../types';
import {
  hiltiMaterialDisposalsAPI,
  hiltiMaterialsAPI,
  hiltiProductsAPI
} from '../services/api';
import { showCustomAlert } from './CustomAlert';

interface FormState {
  productId: string;
  materialId: string;
  disposalDate: Date | null;
  disposalTime: string;
  quantity: number;
  unit: string;
  reason: string;
  notes: string;
}

const HiltiMaterialDisposals: React.FC = () => {
  const [products, setProducts] = useState<HiltiProduct[]>([]);
  const [materialsByProduct, setMaterialsByProduct] = useState<Record<string, HiltiMaterial[]>>({});
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [selectedMaterialId, setSelectedMaterialId] = useState<string>('');
  const [disposals, setDisposals] = useState<HiltiMaterialDisposal[]>([]);
  const [loading, setLoading] = useState(false);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState<HiltiMaterialDisposal | null>(null);
  const [error, setError] = useState('');

  const [form, setForm] = useState<FormState>({
    productId: '',
    materialId: '',
    disposalDate: new Date(),
    disposalTime: '',
    quantity: 0,
    unit: '',
    reason: '',
    notes: ''
  });

  const timeOptions = useMemo(() => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute
          .toString()
          .padStart(2, '0')}`;
        options.push({ label: timeString, value: timeString });
      }
    }
    return options;
  }, []);

  const loadProducts = async () => {
    try {
      const data = await hiltiProductsAPI.getAll();
      setProducts(data);
      if (!selectedProductId && data.length > 0) {
        setSelectedProductId(data[0]._id);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri učitavanju proizvoda';
      showCustomAlert.error(msg);
    }
  };

  const loadMaterials = async (productId: string) => {
    if (!productId || materialsByProduct[productId]) return;
    try {
      const mats = await hiltiMaterialsAPI.getAllForProduct(productId);
      setMaterialsByProduct(prev => ({ ...prev, [productId]: mats }));
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.message || 'Greška pri učitavanju materijala';
      showCustomAlert.error(msg);
    }
  };

  const loadDisposals = async () => {
    if (!selectedProductId) return;
    setLoading(true);
    try {
      const data = await hiltiMaterialDisposalsAPI.getAll({
        productId: selectedProductId,
        materialId: selectedMaterialId || undefined
      });
      setDisposals(data);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.message || 'Greška pri učitavanju rashoda';
      showCustomAlert.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadProducts();
  }, []);

  useEffect(() => {
    if (selectedProductId) {
      void loadMaterials(selectedProductId);
      void loadDisposals();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductId, selectedMaterialId]);

  const materialOptions = useMemo(() => {
    if (!selectedProductId) return [];
    return (materialsByProduct[selectedProductId] || []).map(m => ({
      label: `${m.name} (${m.unit})`,
      value: m._id
    }));
  }, [selectedProductId, materialsByProduct]);

  const productOptions = useMemo(
    () => products.map(p => ({ label: p.name, value: p._id })),
    [products]
  );

  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const qtyBody = (r: HiltiMaterialDisposal) => `${r.quantity} ${r.unit}`;

  const openCreate = () => {
    setEditing(null);
    setError('');
    setForm({
      productId: selectedProductId,
      materialId: selectedMaterialId || '',
      disposalDate: new Date(),
      disposalTime: '',
      quantity: 0,
      unit: '',
      reason: '',
      notes: ''
    });
    setDialogVisible(true);
  };

  const handleSubmit = async () => {
    if (!form.productId || !form.materialId || !form.disposalDate || !form.disposalTime) {
      setError('Proizvod, materijal, datum i vreme su obavezni');
      return;
    }
    if (!form.reason.trim()) {
      setError('Razlog je obavezan');
      return;
    }
    if (form.quantity <= 0) {
      setError('Količina mora biti veća od nule');
      return;
    }

    const payload = {
      productId: form.productId,
      materialId: form.materialId,
      disposalDate: form.disposalDate.toISOString().split('T')[0],
      disposalTime: form.disposalTime,
      quantity: form.quantity,
      unit: form.unit || undefined,
      reason: form.reason.trim(),
      notes: form.notes || undefined
    };

    try {
      setError('');
      await hiltiMaterialDisposalsAPI.create(payload);
      showCustomAlert.success('Rashod je dodat');
      setDialogVisible(false);
      await loadDisposals();
      await loadMaterials(form.productId);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri čuvanju rashoda';
      setError(msg);
      showCustomAlert.error(msg);
    }
  };

  const deleteDisposal = async (r: HiltiMaterialDisposal) => {
    if (!window.confirm('Da li ste sigurni da želite da obrišete ovaj rashod?')) return;
    try {
      await hiltiMaterialDisposalsAPI.delete(r._id);
      await loadDisposals();
      await loadMaterials(
        typeof r.productId === 'string' ? r.productId : (r.productId as HiltiProduct)._id
      );
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.message || 'Greška pri brisanju rashoda';
      showCustomAlert.error(msg);
    }
  };

  return (
    <div style={{ padding: '1.5rem' }}>
      <Card title="Hilti Rashod materijala">
        <div className="flex flex-column md:flex-row gap-3 mb-3">
          <div className="flex flex-column gap-1" style={{ minWidth: '220px' }}>
            <span style={{ fontWeight: 600 }}>Proizvod</span>
            <Dropdown
              value={selectedProductId}
              options={productOptions}
              onChange={(e) => setSelectedProductId(e.value)}
              placeholder="Izaberite proizvod"
            />
          </div>
          <div className="flex flex-column gap-1" style={{ minWidth: '220px' }}>
            <span style={{ fontWeight: 600 }}>Filtriraj po materijalu</span>
            <Dropdown
              value={selectedMaterialId}
              options={[{ label: 'Svi materijali', value: '' }, ...materialOptions]}
              onChange={(e) => setSelectedMaterialId(e.value)}
              placeholder="Svi materijali"
              disabled={!selectedProductId}
            />
          </div>
          <div className="flex align-items-end gap-2">
            <Button
              label="Osveži"
              icon="pi pi-refresh"
              outlined
              onClick={loadDisposals}
              disabled={!selectedProductId}
            />
            <Button
              label="Dodaj rashod"
              icon="pi pi-minus"
              onClick={openCreate}
              disabled={!selectedProductId}
            />
          </div>
        </div>

        {error && !dialogVisible && (
          <div style={{ marginBottom: '1rem' }}>
            <Message severity="error" text={error} />
          </div>
        )}

        <DataTable
          value={disposals}
          loading={loading}
          paginator
          rows={15}
          rowsPerPageOptions={[15, 30, 50]}
          responsiveLayout="stack" breakpoint="768px"
          emptyMessage="Nema rashoda materijala"
        >
          <Column field="materialName" header="Materijal" sortable />
          <Column
            field="disposalDate"
            header="Datum"
            body={(r: HiltiMaterialDisposal) => fmtDate(r.disposalDate)}
            sortable
          />
          <Column field="disposalTime" header="Vreme" sortable />
          <Column field="reason" header="Razlog" />
          <Column header="Količina" body={qtyBody} />
          <Column field="notes" header="Napomene" />
          <Column
            header="Akcije"
            style={{ width: '150px' }}
            body={(r: HiltiMaterialDisposal) => (
              <div className="flex gap-2 justify-content-end">
                <Button
                  icon="pi pi-trash"
                  className="action-icon-btn"
                  text
                  rounded
                  severity="danger"
                  onClick={() => deleteDisposal(r)}
                  tooltip="Obriši"
                />
              </div>
            )}
          />
        </DataTable>
      </Card>

      <Dialog
        header={editing ? 'Izmeni rashod' : 'Novi rashod'}
        visible={dialogVisible}
        style={{ width: '720px', maxWidth: '96vw' }}
        onHide={() => setDialogVisible(false)}
        modal
        closable
      >
        {error && (
          <div style={{ marginBottom: '1rem' }}>
            <Message severity="error" text={error} />
          </div>
        )}

        <div className="grid">
          <div className="col-12 md:col-6">
            <div className="field">
              <label style={{ fontWeight: 600 }}>Proizvod*</label>
              <Dropdown
                value={form.productId}
                options={productOptions}
                onChange={(e) => {
                  const value = e.value as string;
                  setForm(prev => ({ ...prev, productId: value, materialId: '' }));
                  setSelectedProductId(value);
                  setSelectedMaterialId('');
                }}
                placeholder="Izaberite proizvod"
              />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="field">
              <label style={{ fontWeight: 600 }}>Materijal*</label>
              <Dropdown
                value={form.materialId}
                options={materialOptions}
                onChange={(e) =>
                  setForm(prev => ({ ...prev, materialId: e.value as string }))
                }
                placeholder="Izaberite materijal"
                disabled={!form.productId}
              />
            </div>
          </div>

          <div className="col-12 md:col-6">
            <div className="field">
              <label style={{ fontWeight: 600 }}>Datum*</label>
              <Calendar
                value={form.disposalDate}
                onChange={(e) =>
                  setForm(prev => ({ ...prev, disposalDate: e.value as Date | null }))
                }
                dateFormat="dd.mm.yy"
                showIcon
              />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="field">
              <label style={{ fontWeight: 600 }}>Vreme*</label>
              <Dropdown
                value={form.disposalTime}
                options={timeOptions}
                onChange={(e) =>
                  setForm(prev => ({ ...prev, disposalTime: e.value as string }))
                }
                placeholder="Izaberite vreme"
              />
            </div>
          </div>

          <div className="col-12 md:col-6">
            <div className="field">
              <label style={{ fontWeight: 600 }}>Količina*</label>
              <InputNumber
                value={form.quantity}
                onValueChange={(e) =>
                  setForm(prev => ({ ...prev, quantity: e.value || 0 }))
                }
                mode="decimal"
                minFractionDigits={0}
                maxFractionDigits={3}
              />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="field">
              <label style={{ fontWeight: 600 }}>Jedinica</label>
              <InputText
                value={form.unit}
                onChange={(e) =>
                  setForm(prev => ({ ...prev, unit: e.target.value }))
                }
                placeholder="Ostavi prazno za podrazumevanu jedinicu"
              />
            </div>
          </div>

          <div className="col-12">
            <div className="field">
              <label style={{ fontWeight: 600 }}>Razlog*</label>
              <InputText
                value={form.reason}
                onChange={(e) =>
                  setForm(prev => ({ ...prev, reason: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="col-12">
            <div className="field">
              <label style={{ fontWeight: 600 }}>Napomene</label>
              <InputTextarea
                autoResize
                rows={3}
                value={form.notes}
                onChange={(e) =>
                  setForm(prev => ({ ...prev, notes: e.target.value }))
                }
              />
            </div>
          </div>
        </div>

        <div className="flex justify-content-end gap-2 mt-3">
          <Button label="Sačuvaj" icon="pi pi-check" onClick={handleSubmit} />
          <Button
            label="Otkaži"
            icon="pi pi-times"
            severity="secondary"
            outlined
            onClick={() => setDialogVisible(false)}
          />
        </div>
      </Dialog>
    </div>
  );
};

export default HiltiMaterialDisposals;


